import { pageViewTypes } from "@constants/tracking";
import { CHALLENGE_INSTRUCTIONS } from "@features/challenges/constants";
/* -----------------Globals--------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { createSession } from "@features/session/api";
import type { CreateSessionRequest } from "@features/session/api/types";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
/* -----------------Child components--------------- */
import useSessionStore from "@features/session/hooks/useSessionStore";
import SessionContainer from "@features/session/screens/Session";
import { getAuthToken } from "@helpers/auth-provider";
import useAnalytics from "@hooks/useAnalytics";
import { useGlobalStore } from "@hooks/useGlobalStore";
import Box from "@memorang/ui/components/Box";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import Loader from "@memorang/ui/components/Loader";
import { useMutation } from "@tanstack/react-query";
import DocumentTitle from "components/DocumentTitle";
import { Stack } from "expo-router";
import { useEffect, useMemo, useState } from "react";

const ChallengeRoute = () => {
	const eventsObject = useMemo(() => {
		return {
			type: pageViewTypes.session,
		};
	}, []);
	useAnalytics(eventsObject);

	const updateNumericId = useSessionStore((state) => state.updateNumericId);

	const { currentBlock: sessionItems } = useCurrentSession();

	const [fetchingItemDetails, _setFetchingItemDetails] = useState(false);

	const hasNoSessionItems = sessionItems?.length === 0;

	const challengeItem = sessionItems?.[0];

	const isCompleted = challengeItem?.completedAt != null;

	const {
		currentExamContext: {
			projectId,
			organizationId,
			studyPackCollectionId: collectionId,
		},
	} = useExamContext();

	const { mutate } = useMutation({
		mutationFn: async (
			payload: Omit<CreateSessionRequest, "token" | "tenantId">,
		) => {
			const token = await getAuthToken();
			if (!token) {
				throw new Error("No token");
			}
			const response = await createSession({ ...payload });
			return response.id;
		},
		onSuccess: (data: number) => {
			updateNumericId(data);
		},
	});

	useEffect(() => {
		if (!isCompleted) {
			mutate({
				collectionId: collectionId!,
				projectId,
				organizationId,
				tagIds: [],
				sessionType: "FORMATIVE",
				itemsSelected: 10,
				itemsSelectedDistribution: {
					low: 0,
					medium: 0,
					high: 0,
					none: 0,
				},
			});
		}
	}, [mutate, collectionId, projectId, organizationId, isCompleted]);

	// useEffect(() => {
	// 	const fetchDetails = async () => {
	// 		if ((hasNoSessionItems && itemId) || isCompleted) {
	// 			try {
	// 				setFetchingItemDetails(true);

	// 				const res = await getItemDetails({
	// 					itemId: Number.parseInt(itemId),
	// 					userId: userPublicMetadataId!,
	// 				});

	// 				const challengeItem = res.data.items[0];
	// 				const newChallengeItem = {
	// 					...challengeItem,
	// 					media: challengeItem.media?.map((media) => ({
	// 						...media,
	// 						metadata: media.metadata ? safeParseJson(media.metadata) : null,
	// 					})),
	// 				};

	// 				handleCreateFormativeSession({
	// 					challengeItem: newChallengeItem,
	// 					navigate: false,
	// 				});
	// 			} catch (error) {
	// 				console.error("Failed to fetch item details:", error);
	// 			} finally {
	// 				setFetchingItemDetails(false);
	// 			}
	// 		}
	// 	};

	// 	fetchDetails();
	// }, [
	// 	hasNoSessionItems,
	// 	itemId,
	// 	handleCreateFormativeSession,
	// 	isCompleted,
	// 	userPublicMetadataId,
	// 	getToken,
	// 	tenantId,
	// ]);

	const hasShownInstructions = useGlobalStore(
		(state) => state.hasShownChallengeInstructions,
	);
	const setHasShownInstructions = useGlobalStore(
		(state) => state.setHasShownChallengeInstructions,
	);

	const handleDismissInstructions = () => {
		setHasShownInstructions(true);
	};

	if (hasNoSessionItems || fetchingItemDetails) {
		return (
			<Box flex={1} justifyContent="center" alignItems="center">
				<Loader />
			</Box>
		);
	}

	return (
		<>
			<ConfirmDialog
				visible={!hasShownInstructions}
				icon="information-outline"
				title={CHALLENGE_INSTRUCTIONS.title}
				body={CHALLENGE_INSTRUCTIONS.body}
				confirmCta={CHALLENGE_INSTRUCTIONS.confirmCta}
				confirmCallback={handleDismissInstructions}
				showCloseButton={false}
			/>

			<Stack.Screen
				options={{
					headerShown: false,
				}}
			/>
			<DocumentTitle title="Challenge" />
			<SessionContainer />
		</>
	);
};

export default ChallengeRoute;
